import { useState } from 'react';
import { equippedDistricts } from '@neopolis/engine';
import type { Content, GameState } from '@neopolis/engine';

import { T } from '../../textes-interface.js';
import { CarteNeopolis } from '../CarteNeopolis.js';
import { MessageDuMaire } from '../MessageDuMaire.js';
import { CarteIndice } from './CarteIndice.js';

/**
 * Étape 1 — Explorer.
 *
 * L'îlot visite les quartiers, lit les témoignages et les données, et note les
 * problèmes qu'il repère. Il en faut six sur huit pour passer à la suite : de
 * quoi laisser le choix, sans laisser filer la moitié de la ville.
 */
export function EtapeExplorer({
  content,
  partie,
  onDecouvrir,
}: {
  content: Content;
  partie: GameState;
  onDecouvrir: (problemId: string) => void;
}) {
  const [quartierActif, setQuartierActif] = useState<string | null>(null);

  const quartier = content.quartiers.find((q) => q.id === quartierActif) ?? null;
  const problemesDuQuartier = content.problemes.filter((p) => p.quartier === quartierActif);
  const reperes = partie.discoveredProblems.length;

  return (
    <div className="space-y-4">
      <section
        aria-live="polite"
        className="flex flex-wrap items-baseline gap-x-2 rounded-xl bg-white px-4 py-3 shadow-sm"
      >
        <span className="font-semibold text-slate-800">{T.explorer.compteur}</span>
        <span className="text-2xl font-bold tabular-nums text-sky-700">
          {reperes} / {content.problemes.length}
        </span>
        {reperes < content.reglages.problemes_a_reperer_minimum && (
          <span className="text-sm text-slate-600">
            {T.explorer.minimum} {content.reglages.problemes_a_reperer_minimum}{' '}
            {T.explorer.pourAvancer}
          </span>
        )}
      </section>

      <CarteNeopolis
        quartiers={content.quartiers}
        quartiersEquipes={equippedDistricts(partie, content)}
        quartierActif={quartierActif}
        onChoisir={(id) => setQuartierActif((precedent) => (precedent === id ? null : id))}
      />

      {quartier && (
        <MessageDuMaire titre={quartier.nom} onFermer={() => setQuartierActif(null)}>
          <p>{quartier.description}</p>

          {problemesDuQuartier.length === 0 ? (
            <p className="italic text-slate-600">{T.explorer.quartierVide}</p>
          ) : (
            <ul className="mt-3 space-y-4">
              {problemesDuQuartier.map((probleme) => {
                const repere = partie.discoveredProblems.includes(probleme.id);

                return (
                  <li key={probleme.id} className="rounded-2xl border border-slate-200 p-3">
                    <h3 className="text-lg font-bold text-slate-900">{probleme.titre}</h3>

                    <ul className="mt-2 space-y-2">
                      {probleme.indices.map((indice) => (
                        <CarteIndice key={indice.texte} indice={indice} />
                      ))}
                    </ul>

                    <button
                      type="button"
                      disabled={repere}
                      onClick={() => onDecouvrir(probleme.id)}
                      className={`mt-3 min-h-touch w-full rounded-xl px-4 font-semibold ${
                        repere
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-sky-600 text-white hover:bg-sky-700'
                      }`}
                    >
                      {repere ? (
                        <>
                          <span aria-hidden="true">✓ </span>
                          {T.explorer.repere}
                        </>
                      ) : (
                        T.explorer.reperer
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </MessageDuMaire>
      )}
    </div>
  );
}
